import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getSocketUserChat } from "../Funcs/APIFuncs/socketUserChat";
import { isAlarmNotificationData, buildAlarmStateFromPushData } from "../utils/alarmUtils";
import axiosInstance from "../Funcs/axiosFuncs/axiosInstance";
import { resolveApiBaseUrl } from "../config/resolveApiBaseUrl";

const SEEN_TTL_MS = 120_000;
const seen = new Map();

function pruneSeen() {
  const now = Date.now();
  for (const [k, t] of seen) {
    if (now - t > SEEN_TTL_MS) seen.delete(k);
  }
}

function markPushMessageDelivered(messageId) {
  const id = String(messageId ?? "").trim();
  if (!id) return;
  pruneSeen();
  seen.set(id, Date.now());
}

function isPushMessageAlreadyDelivered(messageId) {
  const id = String(messageId ?? "").trim();
  if (!id) return false;
  pruneSeen();
  return seen.has(id);
}

const AlarmContext = createContext(null);

let ringtoneAudio = null;
let audioUnlocked = false;

function ensureRingtoneAudio() {
  if (ringtoneAudio) return;
  try {
    ringtoneAudio = new Audio("/Sounds/doitify_morning_wake.wav");
    ringtoneAudio.volume = 0.7;
    ringtoneAudio.preload = "auto";
    ringtoneAudio.loop = true;
  } catch {
    /* ignore */
  }
}

export function unlockAudio() {
  if (typeof window === "undefined") return true;
  if (audioUnlocked) return true;

  ensureRingtoneAudio();
  if (!ringtoneAudio) return false;

  ringtoneAudio.volume = 0;
  try {
    const p = ringtoneAudio.play();
    audioUnlocked = true;
    ringtoneAudio.pause();
    ringtoneAudio.currentTime = 0;
    ringtoneAudio.volume = 0.7;
    if (p && typeof p.then === "function") {
      return p
        .then(() => {
          ringtoneAudio.pause();
          ringtoneAudio.currentTime = 0;
          return true;
        })
        .catch(() => {
          audioUnlocked = false;
          return false;
        });
    }
  } catch {
    /* ignore */
  }
  return true;
}

export function startRingtone() {
  ensureRingtoneAudio();
  if (!ringtoneAudio) return;
  unlockAudio();
  try {
    ringtoneAudio.currentTime = 0;
    ringtoneAudio.play().catch(() => {});
  } catch {
    /* ignore */
  }
}

export function stopRingtone() {
  if (ringtoneAudio) {
    try {
      ringtoneAudio.pause();
      ringtoneAudio.currentTime = 0;
    } catch {
      /* ignore */
    }
  }
}

const SNOOZE_PRESETS_MS = [
  5 * 1000,
  5 * 60 * 1000,
  30 * 60 * 1000,
  60 * 60 * 1000,
  120 * 60 * 1000,
];

export { SNOOZE_PRESETS_MS };

function getAPIBase() {
  return resolveApiBaseUrl();
}

const API = getAPIBase();

async function changeUserNotifReadStatus(notifId) {
  try {
    await axiosInstance.put(`${API}user/ChangeUserNotifReadStatus/${notifId}`, {});
  } catch {
    /* best-effort */
  }
}

export function AlarmProvider({ children }) {
  // SSR-safe: localStorage doesn't exist on the server.
  const userId = useRef(
    typeof localStorage === "undefined" ? null : localStorage.getItem("UserID")
  );
  const [alarm, setAlarm] = useState(null);
  const snoozeTimerRef = useRef(null);

  const clearSnoozeTimer = useCallback(() => {
    if (snoozeTimerRef.current != null) {
      clearTimeout(snoozeTimerRef.current);
      snoozeTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearSnoozeTimer();
  }, [clearSnoozeTimer]);

  useEffect(() => {
    const uid = localStorage.getItem("UserID");
    if (!uid) return undefined;

    const onRelay = (relay) => {
      const dataRaw =
        relay?.data && typeof relay.data === "object" ? relay.data : {};
      const mid =
        relay?.messageId ?? dataRaw.messageId ?? dataRaw.MessageId;
      if (!mid || isPushMessageAlreadyDelivered(mid)) return;

      const title = String(
        relay?.title ?? dataRaw.title ?? dataRaw.Title ?? "Doitify",
      );
      const body = String(relay?.body ?? dataRaw.body ?? dataRaw.Body ?? "");
      const data = { ...dataRaw };

      markPushMessageDelivered(mid);

      if (!isAlarmNotificationData(data)) return;

      clearSnoozeTimer();
      setAlarm(
        buildAlarmStateFromPushData(data, title, body, mid),
      );
    };

    let detach = () => {};
    let cancelled = false;

    const tryAttach = () => {
      detach();
      const socket = getSocketUserChat();
      if (!socket) return false;
      socket.on("push:relay", onRelay);
      detach = () => socket.off("push:relay", onRelay);
      return true;
    };

    if (tryAttach()) {
      return () => {
        cancelled = true;
        detach();
      };
    }

    const interval = setInterval(() => {
      if (cancelled) return;
      if (tryAttach()) clearInterval(interval);
    }, 120);
    const stop = setTimeout(() => clearInterval(interval), 12000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(stop);
      detach();
    };
  }, [clearSnoozeTimer]);

  const dismiss = useCallback(() => {
    clearSnoozeTimer();
    stopRingtone();
    const alarmId = alarm?.id;
    if (alarmId) {
      changeUserNotifReadStatus(alarmId);
    }
    setAlarm(null);
  }, [alarm?.id, clearSnoozeTimer]);

  const snooze = useCallback(
    (ms) => {
      const snapshot = alarm;
      if (!snapshot?.title) {
        dismiss();
        return;
      }

      clearSnoozeTimer();
      stopRingtone();
      setAlarm(null);

      snoozeTimerRef.current = setTimeout(() => {
        snoozeTimerRef.current = null;
        setAlarm({
          id: snapshot.id,
          title: snapshot.title,
          body: snapshot.body,
          voiceUrls: snapshot.voiceUrls ?? [],
          boardId: snapshot.boardId,
          taskId: snapshot.taskId,
          taskMapId: snapshot.taskMapId,
          messageId: snapshot.messageId,
        });
      }, ms);
    },
    [alarm, dismiss, clearSnoozeTimer],
  );

  const value = useMemo(
    () => ({ alarm, dismiss, snooze }),
    [alarm, dismiss, snooze],
  );

  return (
    <AlarmContext.Provider value={value}>{children}</AlarmContext.Provider>
  );
}

export function useAlarm() {
  return useContext(AlarmContext);
}
